import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "../axios";
import toast from "../hooks/toast";
import { buildApiV3Url, getTodayLocalDateString, previousDays } from "../shared/Utils";
import type { RootState } from "./types";

// -----------------------------------------------------------------------------
// Bounced email report (Admin Reports).
// GET /api/v3/companies/{companyId}/bounced-emails-report — plain query-string params
// (page/pageSize, not OData $filter/$top/$skip), defaults to the last 30 days server-side;
// the requested date range must not exceed 90 days.
// -----------------------------------------------------------------------------

export interface BouncedEmailRow {
    id: string;
    customerCode: string;
    customerName: string;
    timestamp: string;
    recipient: string;
    emailSource: string;
    deliveryStatus: string;
    reason: string;
}

export interface PaginateState {
    totalRecords: number;
    startIndex: number;
    endIndex: number;
    rowsPerPage: number;
}

export interface PageState {
    data: BouncedEmailRow[];
    paginate: PaginateState;
}

export const EMAIL_SOURCES = [
    { label: "All sources", value: "" },
    { label: "Campaign", value: "Campaign" },
    { label: "Single request", value: "SingleRequest" },
] as const;

export const getEmailSourceOptions = (selectedSource: string) =>
    EMAIL_SOURCES.map(option => ({
        ...option,
        selected: option.value === selectedSource,
    }));

export const EMAIL_DELIVERY_STATUS = [
    { label: "All Delivery Status", value: "" },
    { label: "Bounce", value: "Bounce" },
    { label: "Dropped", value: "Dropped" },
    { label: "SpamReport", value: "SpamReport" },
    { label: "Unsubscribe", value: "Unsubscribe" },
    { label: "GroupUnsubscribe", value: "GroupUnsubscribe" },
] as const;

// Mirrors the API's BouncedEmailReportDeliveryStatusFilter enum; "" means unset.
export type BouncedEmailDeliveryStatusFilter = (typeof EMAIL_DELIVERY_STATUS)[number]["value"];

export const getEmailDeliveryStatusOptions = (selectedEmailDelivery: string) =>
    EMAIL_DELIVERY_STATUS.map(option => ({
        ...option,
        selected: option.value === selectedEmailDelivery,
    }));

// Mirrors the API's BouncedEmailReportSourceFilter enum (Campaign | SingleRequest); "" means unset.
export type BouncedEmailSourceFilter = (typeof EMAIL_SOURCES)[number]["value"];

export interface BouncedEmailReportFormState {
    startDate: string;
    endDate: string;
    searchText: string;
    emailSource: BouncedEmailSourceFilter;
    deliveryStatus: BouncedEmailDeliveryStatusFilter;
    reason: string;
}

export interface BouncedEmailReportState {
    isLoading: boolean;
    page: PageState;
    formState: BouncedEmailReportFormState;
    isFiltersPanelOpen: boolean;
}

// Shape of the API response (camelCase JSON from the .NET Page<BouncedEmailReportViewModel>).
interface ApiBouncedEmail {
    customerCode: string | null;
    customerName: string | null;
    timestamp: string | null;
    recipient: string | null;
    emailSource: string | null;
    deliveryStatus: string | null;
    reason: string | null;
}
interface ApiPage {
    value?: ApiBouncedEmail[];
    count?: number;
}

const emptyPage: PageState = {
    data: [],
    paginate: {
        totalRecords: 0,
        startIndex: 0,
        endIndex: 0,
        rowsPerPage: 0,
    },
};

const initialState: BouncedEmailReportState = {
    isLoading: false,
    page: { ...emptyPage },
    formState: {
        startDate: previousDays(30),
        endDate: getTodayLocalDateString(),
        searchText: "",
        emailSource: "",
        deliveryStatus: "",
        reason: "",
    },
    isFiltersPanelOpen: false,
};

// =============================================================================
// SELECTORS
// =============================================================================

export const selectIsLoading = (state: RootState): boolean => state.bouncedEmailReport.isLoading;
export const selectPage = (state: RootState): PageState => state.bouncedEmailReport.page;
export const selectFormState = (state: RootState): BouncedEmailReportFormState =>
    state.bouncedEmailReport.formState;
export const selectIsFiltersPanelOpen = (state: RootState): boolean =>
    state.bouncedEmailReport.isFiltersPanelOpen;

// =============================================================================
// REQUEST/RESPONSE HELPERS
// =============================================================================

// Builds the bounced-emails-report query string from the literal params the endpoint accepts
// (not OData $filter — see api_spec.json). Omits empty/undefined values entirely.
export const buildBouncedEmailReportQuery = (
    formState: BouncedEmailReportFormState,
    page: number,
    pageSize: number
): string => {
    const params = new URLSearchParams();
    if (formState.startDate) {
        params.append("startDate", formState.startDate);
    }
    if (formState.endDate) {
        params.append("endDate", formState.endDate);
    }
    if (formState.searchText?.trim()) {
        params.append("searchText", formState.searchText.trim());
    }
    if (formState.emailSource) {
        params.append("emailSource", formState.emailSource);
    }
    if (formState.deliveryStatus?.trim()) {
        params.append("deliveryStatus", formState.deliveryStatus.trim());
    }
    if (formState.reason?.trim()) {
        params.append("reason", formState.reason.trim());
    }
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    return params.toString();
};

// The endpoint rejects a startDate/endDate span wider than 90 days (see file header). Validate
// client-side so an over-wide range is caught before hitting the API with a specific message,
// instead of surfacing as the thunk's generic "Failed to fetch" toast.
export const MAX_DATE_RANGE_DAYS = 90;

export const validateBouncedEmailDateRange = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) {
        return "";
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return "";
    }
    if (start > end) {
        return "From date must be on or before the To date.";
    }
    const spanDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
    if (spanDays > MAX_DATE_RANGE_DAYS) {
        return `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days.`;
    }
    return "";
};

const toBouncedEmailRow = (item: ApiBouncedEmail, index: number): BouncedEmailRow => ({
    // The API has no stable row identifier; index is scoped to a single page render.
    id: String(index),
    customerCode: item.customerCode ?? "",
    customerName: item.customerName ?? "",
    timestamp: item.timestamp ?? "",
    recipient: item.recipient ?? "",
    emailSource: item.emailSource ?? "",
    deliveryStatus: item.deliveryStatus ?? "",
    reason: item.reason ?? "",
});

// =============================================================================
// THUNK
// =============================================================================

interface FetchBouncedEmailReportArgs {
    pageSize?: number;
    pageNumber?: number;
}

// A superseded (aborted) request rejects with "aborted" so the reducer can leave the newer
// request's state untouched; a genuine failure rejects with "error".
type FetchReject = "aborted" | "error";

// Tracks the in-flight request so a new fetch cancels the previous one (latest wins).
let inFlightController: AbortController | null = null;

// Aborts any in-flight fetch. Call on unmount so a late response can't fulfill after the
// user has navigated away and refill Redux with stale/wrong-page rows.
export const abortBouncedEmailReportFetch = (): void => {
    inFlightController?.abort();
};

export const fetchBouncedEmailReport = createAsyncThunk<
    PageState,
    FetchBouncedEmailReportArgs | undefined,
    { state: RootState; rejectValue: FetchReject }
>("bouncedEmailReport/fetch", async (args, { getState, rejectWithValue }) => {
    const { pageSize, pageNumber } = args ?? {};
    const { formState } = getState().bouncedEmailReport;
    const { activeCompany, rowsPerPage } = getState().session;

    inFlightController?.abort();
    const controller = new AbortController();
    inFlightController = controller;

    const finalPageSize = pageSize ?? rowsPerPage ?? 20;
    const finalPageNumber = pageNumber ?? 1;
    const startIndex = (finalPageNumber - 1) * finalPageSize;

    try {
        const query = buildBouncedEmailReportQuery(formState, finalPageNumber, finalPageSize);
        const relativeUrl = `companies/${activeCompany.id}/bounced-emails-report`;
        const baseUrl = buildApiV3Url(relativeUrl);
        const url = `${baseUrl}?${query}`;

        const { data: apiPage } = await axios.get<ApiPage>(url, {
            withCredentials: true,
            signal: controller.signal,
        });

        const data = (apiPage.value ?? []).map(toBouncedEmailRow);
        return {
            data,
            paginate: {
                totalRecords: apiPage.count ?? 0,
                startIndex,
                endIndex: startIndex + data.length,
                rowsPerPage: finalPageSize,
            },
        };
    } catch {
        // Superseded by a newer fetch — let that request own the loading/page state, no toast.
        if (controller.signal.aborted) {
            return rejectWithValue("aborted");
        }
        const [showToast] = toast();
        showToast("error", "Failed to fetch Bounced Email report.");
        return rejectWithValue("error");
    } finally {
        if (inFlightController === controller) {
            inFlightController = null;
        }
    }
});

// =============================================================================
// REDUCERS
// =============================================================================

const bouncedEmailReportSlice = createSlice({
    name: "bouncedEmailReport",
    initialState,
    reducers: {
        setPageToNull: state => {
            state.page = { ...emptyPage };
        },
        setFormField: (
            state,
            action: PayloadAction<{ field: keyof BouncedEmailReportFormState; value: string }>
        ) => {
            const { field, value } = action.payload;
            state.formState = { ...state.formState, [field]: value };
        },
        setIsFiltersPanelOpen: (state, action: PayloadAction<boolean>) => {
            state.isFiltersPanelOpen = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchBouncedEmailReport.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchBouncedEmailReport.fulfilled, (state, action) => {
                state.isLoading = false;
                state.page = action.payload;
            })
            .addCase(fetchBouncedEmailReport.rejected, (state, action) => {
                if (action.payload === "aborted") {
                    return;
                }
                state.isLoading = false;
                state.page = { ...emptyPage };
            });
    },
});

export const { setPageToNull, setFormField, setIsFiltersPanelOpen } =
    bouncedEmailReportSlice.actions;

export default bouncedEmailReportSlice.reducer;
